import React, { useEffect, useMemo, useState } from "react";
import {
  Line, LineChart, Area, AreaChart, Bar, BarChart, Pie, PieChart, Cell,
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from "recharts";
import {
  TrendingUp, Users, Gem, MessageSquare, Timer, Eye, Loader2, DollarSign,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const RANGES = [
  { key: "7d",  label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "all", label: "Lifetime" },
];

const TYPE_LABELS = {
  tip: "Tips",
  channel_sub_gc: "Subs (GC)",
  channel_sub_stripe: "Subs (Stripe)",
};
const TYPE_COLORS = {
  tip: "var(--sc-pink)",
  channel_sub_gc: "var(--sc-cyan)",
  channel_sub_stripe: "var(--sc-volt)",
};

const AXIS_STYLE = { fill: "#71717a", fontSize: 10, fontFamily: "monospace" };

export default function Analytics() {
  const { user } = useAuth();
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get(`/streamer/analytics/${user.username}`, { params: { range } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user?.username, range]);

  const revenueData = useMemo(() => (data?.revenue_series || []).map((r) => ({
    date: r.date.slice(5),
    revenue: r.revenue_cents / 100,
    gross: r.gross_cents / 100,
    tx: r.transactions,
  })), [data]);

  const growthData = useMemo(() => {
    const subs = data?.subs_series || [];
    const follows = data?.followers_series || [];
    return subs.map((s, i) => ({
      date: s.date.slice(5),
      subs: s.new_subs,
      followers: (follows[i]?.new_followers) || 0,
    }));
  }, [data]);

  const chatData = useMemo(() => (data?.chat_series || []).map((c) => ({
    date: c.date.slice(5),
    messages: c.messages,
    unique: c.unique_chatters,
  })), [data]);

  const sessionData = useMemo(() => (data?.session_series || []).map((s) => ({
    date: s.date.slice(5),
    hours: s.hours,
    peak: s.peak_viewers,
    sessions: s.sessions,
  })), [data]);

  const revenueByTypePie = useMemo(() => (data?.revenue_by_type || []).map((r) => ({
    name: TYPE_LABELS[r.type] || r.type,
    value: r.cents / 100,
    fill: TYPE_COLORS[r.type] || "var(--sc-orange)",
  })), [data]);

  if (loading) {
    return <div className="term-card p-10 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-zinc-500" /></div>;
  }

  if (!data) {
    return <div className="term-card p-10 text-center text-sm text-zinc-500">No analytics available yet.</div>;
  }

  const h = data.headline;

  return (
    <div className="space-y-6" data-testid="section-analytics">
      {/* Range selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1" data-testid="analytics-range-group">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-xs sc-mono rounded transition ${range === r.key ? "bg-[color:var(--sc-cyan)]/15 text-[color:var(--sc-cyan)] border border-[color:var(--sc-cyan)]/40" : "text-zinc-500 hover:text-zinc-200 border border-transparent"}`}
              data-testid={`analytics-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="text-[10px] sc-mono text-zinc-500">Last updated {new Date().toLocaleTimeString()}</div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Head icon={DollarSign}    color="volt"   label="REVENUE"        value={`$${h.revenue_usd.toFixed(2)}`} testid="analytics-head-revenue" />
        <Head icon={Gem}           color="pink"   label="NEW SUBS"       value={h.new_subscribers}              testid="analytics-head-subs" />
        <Head icon={Users}         color="cyan"   label="NEW FOLLOWERS"  value={h.new_followers}                testid="analytics-head-followers" />
        <Head icon={Eye}           color="orange" label="PEAK VIEWERS"   value={h.peak_viewers}                 testid="analytics-head-peak" />
        <Head icon={Timer}         color="cyan"   label="HOURS STREAMED" value={h.hours_streamed}               testid="analytics-head-hours" />
        <Head icon={MessageSquare} color="pink"   label="CHAT MESSAGES"  value={h.chat_messages.toLocaleString()} testid="analytics-head-chat" />
      </div>

      {/* Revenue growth (area) */}
      <ChartCard title="REVENUE GROWTH" color="volt" testid="chart-revenue">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={revenueData}>
            <defs>
              <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--sc-volt)" stopOpacity={0.5} />
                <stop offset="100%" stopColor="var(--sc-volt)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1f1f24" vertical={false} />
            <XAxis dataKey="date" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `$${v}`} />
            <Tooltip contentStyle={{ background: "#0F0F14", border: "1px solid #2a2a30", fontFamily: "monospace", fontSize: 12 }} formatter={(v) => `$${v}`} />
            <Area type="monotone" dataKey="revenue" stroke="var(--sc-volt)" strokeWidth={2} fill="url(#revenueFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Growth (subs + followers) */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="SUBS & FOLLOWERS" color="cyan" testid="chart-growth">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={growthData}>
              <CartesianGrid stroke="#1f1f24" vertical={false} />
              <XAxis dataKey="date" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: "#0F0F14", border: "1px solid #2a2a30", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 10 }} />
              <Line type="monotone" dataKey="subs" name="New subs" stroke="var(--sc-pink)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="followers" name="Followers" stroke="var(--sc-cyan)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="REVENUE MIX" color="pink" testid="chart-revenue-mix">
          {revenueByTypePie.length === 0 ? (
            <div className="h-60 grid place-items-center text-xs text-zinc-500">No revenue in this range</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={revenueByTypePie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={4}>
                  {revenueByTypePie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0F0F14", border: "1px solid #2a2a30", fontSize: 12 }} formatter={(v) => `$${v.toFixed(2)}`} />
                <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Chat activity + Stream hours */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="CHAT ACTIVITY" color="pink" testid="chart-chat">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chatData}>
              <CartesianGrid stroke="#1f1f24" vertical={false} />
              <XAxis dataKey="date" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: "#0F0F14", border: "1px solid #2a2a30", fontSize: 12 }} />
              <Bar dataKey="messages" fill="var(--sc-pink)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="STREAM HOURS" color="orange" testid="chart-hours">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sessionData}>
              <CartesianGrid stroke="#1f1f24" vertical={false} />
              <XAxis dataKey="date" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: "#0F0F14", border: "1px solid #2a2a30", fontSize: 12 }} formatter={(v, k) => k === "hours" ? `${v}h` : v} />
              <Bar dataKey="hours" fill="var(--sc-orange)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top lists */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ListCard title="TOP TIPPERS" color="volt" testid="list-top-tippers"
          rows={(data.top_tippers || []).map((t) => ({
            key: t.username,
            left: t.username,
            right: `$${(t.cents / 100).toFixed(2)}`,
            sub: `${t.tips} tip${t.tips === 1 ? "" : "s"}`,
          }))}
          empty="No tips in this range."
        />
        <ListCard title="TOP STREAMS" color="cyan" testid="list-top-streams"
          rows={(data.top_streams || []).map((s) => ({
            key: s.id,
            left: s.title || "(untitled)",
            right: `${s.peak_viewers || 0} peak`,
            sub: s.category || "",
          }))}
          empty="No archived streams yet."
        />
      </div>
    </div>
  );
}

function Head({ icon: Icon, color, label, value, testid }) {
  return (
    <div className={`term-card ${color} p-3`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3" style={{ color: `var(--sc-${color})` }} />
        <span className="sc-label text-[9px]" style={{ color: `var(--sc-${color})` }}>{label}</span>
      </div>
      <div className="sc-heading text-xl font-black">{value}</div>
    </div>
  );
}

function ChartCard({ title, color, children, testid }) {
  return (
    <div className={`term-card ${color} p-4`} data-testid={testid}>
      <div className="sc-label mb-3" style={{ color: `var(--sc-${color})` }}>◤ {title}</div>
      {children}
    </div>
  );
}

function ListCard({ title, color, rows, empty, testid }) {
  return (
    <div className={`term-card ${color} p-4`} data-testid={testid}>
      <div className="sc-label mb-3" style={{ color: `var(--sc-${color})` }}>◤ {title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-500 py-6 text-center">{empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r, i) => (
            <li key={r.key || i} className="flex items-center justify-between p-2 rounded hover:bg-white/5 text-sm">
              <div className="min-w-0">
                <div className="text-zinc-200 truncate">{r.left}</div>
                {r.sub && <div className="text-[10px] sc-mono text-zinc-500">{r.sub}</div>}
              </div>
              <div className="sc-mono font-bold ml-3 shrink-0" style={{ color: `var(--sc-${color})` }}>{r.right}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
